<script setup>
import { capitalize, ratio } from '@/helpers'
import { Head, Link } from '@inertiajs/vue3'
import Breadcrumb from '@/Frontend/Components/Breadcrumb.vue'
import { thumbor } from '@/Frontend/plugins/thumbor/thumbor'

// const breadcrumb = defineAsyncComponent(() => import('@components/Breadcrumb.vue'))

const props = defineProps({
	recipe: {
		type: Object,
		default: () => {},
	},
	moreRecipes: {
		type: Array,
		default: () => [],
	},
})

const appStore = useAppStore()
const prepTimeComputed = computed(() => {
	// Add minutes if only digits are filled in
	return /^\d+$/.test(props.recipe.prep_time) ? `${props.recipe.prep_time} min.` : props.recipe.prep_time.replace('uten', '.')
})

const personsComputed = computed(() => {
	// Add persons if only digits are filled in
	return /^\d+$/.test(props.recipe.persons) ? `${props.recipe.persons} pers.` : props.recipe.persons.replace('onen', '.')
})

const articleImg = {
	sm: {
		dpr1: thumbor(props.recipe.thumbnail).dimension(390, 507).filters('format(webp)').url(),
		dpr2: thumbor(props.recipe.thumbnail)
			.dimension(390 * 2, 507 * 2)
			.filters('format(webp)')
			.url(),
	},
	md: {
		dpr1: thumbor(props.recipe.thumbnail).dimension(425, 560).filters('format(webp)').url(),
		dpr2: thumbor(props.recipe.thumbnail)
			.dimension(425 * 2, 560 * 2)
			.filters('format(webp)')
			.url(),
	},
}

// console.log(articleImg)

// Add SEO
const pageMeta = [
	{ tag: 'link', tagItem: 'rel', tagValue: 'canonical', contentItem: 'href', contentValue: `https://lekker.nl/recepten/${props.recipe.slug}` },
	{ tag: 'meta', tagItem: 'name', tagValue: 'title', contentItem: 'content', contentValue: props.recipe.title },
	{ tag: 'meta', tagItem: 'name', tagValue: 'description', contentItem: 'content', contentValue: props.recipe.description },
	{ tag: 'meta', tagItem: 'name', tagValue: 'keywords', contentItem: 'content', contentValue: props.recipe.tags },
	{ tag: 'meta', tagItem: 'property', tagValue: 'article:published_time', contentItem: 'content', contentValue: props.recipe.published_at?.replace(' ', 'T') },
	{ tag: 'meta', tagItem: 'property', tagValue: 'og:title', contentItem: 'content', contentValue: props.recipe.title },
	{ tag: 'meta', tagItem: 'property', tagValue: 'og:description', contentItem: 'content', contentValue: props.recipe.description },
	{ tag: 'meta', tagItem: 'property', tagValue: 'og:image', contentItem: 'content', contentValue: thumbor(props.recipe.thumbnail).dimension(1200, 0).filters('format(webp)').url() },
	{ tag: 'meta', tagItem: 'name', tagValue: 'twitter:title', contentItem: 'content', contentValue: `${props.recipe.title} - Lekker` },
	{ tag: 'meta', tagItem: 'name', tagValue: 'twitter:card', contentItem: 'content', contentValue: 'summary' },
	{ tag: 'meta', tagItem: 'name', tagValue: 'twitter:site', contentItem: 'content', contentValue: '@lekker500' },
]
</script>

<template>
	<Head>
		<title>{{ recipe.title }}</title>
		<link
			rel="preload"
			as="image"
			:href="articleImg.sm.dpr1"
			:imagesrcset="`${articleImg.sm.dpr1} 1x, ${articleImg.sm.dpr2} 2x`"
			:media="`max-width: ${appStore.breakpoints['sm']}px`"
			type="image/webp"
			fetchpriority="high"
		/>
		<link
			rel="preload"
			as="image"
			:href="articleImg.md.dpr1"
			:imagesrcset="`${articleImg.md.dpr1} 1x, ${articleImg.md.dpr2} 2x`"
			:media="`min-width: ${appStore.breakpoints['sm'] + 1}px`"
			type="image/webp"
			fetchpriority="high"
		/>
		<component
			:is="`${meta.tag}`"
			v-for="(meta, metaIndex) in pageMeta"
			:key="`pagemeta-${metaIndex}`"
			:[meta.tagItem]="meta.tagValue"
			:[meta.contentItem]="meta.contentValue"
		/>

		<component
			:is="`script`"
			type="application/ld+json"
		>
			{{ recipe.schema }}
		</component>
	</Head>

	<main>
		<article
			class="recipe mt-lg-4"
			:data-article-id="recipe.id"
		>
			<header class="container-xxl main-wrapper">
				<div class="row justify-content-center">
					<div class="col-xl-10">
						<div class="recipe-header mx-n4 mx-lg-0">
							<nav aria-label="breadcrumb">
								<client-only>
									<breadcrumb
										:nav-items="[
											{ label: 'Home', route: route('home') },
											{ label: 'Recepten', route: route('recipes') },
										]"
										:current="recipe.title"
										class="m-4 m-lg-0"
									/>
								</client-only>
							</nav>
							<div class="head">
								<h1 class="ff-serif">{{ recipe.title }}</h1>
								<div class="mb-3">
									<span v-if="recipe.prep_time">
										<i class="str-time" />
										{{ prepTimeComputed }}
									</span>
									<i
										v-if="recipe.prep_time && recipe.persons"
										class="str-diamond"
									/>
									<span v-if="recipe.persons">
										<i class="str-persons" />
										{{ personsComputed }}
									</span>
								</div>
								<div>
									<ul class="article-tags list-unstyled d-flex flex-wrap gap-1 gap-md-2 mt-2">
										<li
											v-for="tag in recipe.tags.split(',')"
											:key="`tag-${tag}`"
										>
											<Link :href="route('tags', { slug: tag })">
												<theme-property-badge hover>
													{{ capitalize(tag) }}
												</theme-property-badge>
											</Link>
										</li>
									</ul>
								</div>
							</div>
							<figure class="figure recipe-img position-relative mb-0">
								<picture class="picture">
									<source
										:srcset="`${articleImg.sm.dpr1} 1x, ${articleImg.sm.dpr2} 2x`"
										:media="`(max-width: ${appStore.breakpoints['sm']}px)`"
										type="image/webp"
										width="390"
										height="507"
									/>
									<source
										:srcset="`${articleImg.md.dpr1} 1x, ${articleImg.md.dpr2} 2x`"
										:media="`(min-width: ${appStore.breakpoints['sm'] + 1}px)`"
										type="image/webp"
										width="425"
										height="560"
									/>
									<img
										:src="articleImg.md.dpr1"
										class="img-fluid w-100 rounded"
										width="425"
										height="560"
										:alt="recipe.thumbnail_alt ?? ''"
										fetchpriority="high"
									/>
								</picture>
							</figure>
							<!-- <thumbor-image
								:use="['sm', 'md']"
								:dimensions="{
									sm: [500, 650],
									md: [425, 560],
								}"
								:img="recipe.thumbnail"
								class="recipe-img position-relative mb-0"
								:classes="{
									img: 'w-100 rounded',
								}"
								filters="format(webp)"
							/> -->
						</div>
					</div>
				</div>
			</header>

			<main class="container-xxl main-wrapper mt-3 mt-lg-5">
				<div class="row justify-content-center">
					<div class="col-lg-9 col-xl-6">
						<p class="lead mb-0">{{ recipe.description }}</p>
					</div>
					<div class="w-100"></div>
					<div class="col-lg-9 col-xl-6">
						<hr class="my-lg-5" />
						<h4 class="mb-lg-4">{{ $t('Ingredients') }}</h4>
						<ul class="list list-check mb-0">
							<li
								v-for="(ingredient, ingredientIndex) in recipe.ingredients"
								:key="`ingredient-${ingredientIndex}`"
							>
								{{ ingredient }}
							</li>
						</ul>
					</div>
					<div class="w-100"></div>
					<div class="col-lg-9 col-xl-6">
						<hr class="my-lg-5" />
						<h4 class="mb-lg-4">{{ $t('Method of preparation') }}</h4>
						<ul class="list list-steps mb-0">
							<li
								v-for="(step, stepIndex) in recipe.steps"
								:key="`step-${stepIndex}`"
							>
								<div class="d-flex">
									<span>
										<img
											v-if="recipe.images[stepIndex]"
											:src="$thumbor(recipe.images[stepIndex]).dimension(100, 100).smart().url()"
											class="rounded me-3"
										/>
									</span>
									<span v-html-raw="step"></span>
								</div>
							</li>
						</ul>
					</div>
				</div>
			</main>
		</article>
	</main>

	<client-only>
		<aside class="py-3 py-md-5 bg-neutral-200 mt-3">
			<div class="container-xxl main-wrapper">
				<div class="row justify-content-center">
					<div class="col-lg-10">
						<!-- Recipes -->
						<theme-base-swiper
							v-slot="slotProps"
							tag="section"
							:title="$t('Continue cooking')"
							:items="moreRecipes"
							:columns="{
								xs: 6,
								md: 4,
								lg: 3,
							}"
							url="/recepten"
							inline
						>
							<theme-card-recipe
								tag="article"
								:item="slotProps.item"
								style="--card-bg: rgba(255, 255, 255, 0.1)"
							>
								<template #img="{ meta }">
									<thumbor-image
										:use="['sm', 'md']"
										:dimensions="{
											sm: [163, ratio(163, 1, 1.35)],
											md: [250, ratio(250, 1, 1.35)],
										}"
										:img="meta.imgSource"
										class="w-100 mb-0"
										:classes="{
											img: 'custom-card-img image-fluid bg-neutral-300 w-100',
										}"
										:img-alt="meta.imgAlt ?? null"
										:crop-preset="meta.imgCropping"
										filters="format(webp)"
									/>
								</template>
							</theme-card-recipe>
						</theme-base-swiper>
					</div>
				</div>
			</div>
		</aside>
	</client-only>
</template>
